export const BASE_CHART_HEIGHT = 320;
export const DISABLED_OPACITY = 0.1;

export interface iViewBoxProps {
    canvasWidth: number;
    canvasHeight: number;
    width: number;
    height: number;
    padding: {
        top: number;
        bottom: number;
        left: number;
        right: number;
    };
    proportion: number;
}

export enum PointType {
    Small,
    Solid,
    Empty,
}

// New dynamic data structure
export interface iLineChartDataPoint {
    value: number;
    label?: string; // For sub-points like "Period 1", "Week 1", etc.
    isActual?: boolean; // Whether this point represents actual vs predicted data
}

export interface iLineChartTimePoint {
    label: string; // Time period label like "2023"
    tooltipSubTitle?: string; // Optional subtitle for tooltip
    points: iLineChartDataPoint[]; // Array of points (1 for single period, n for sub-periods)
    isDashed?: boolean; // Whether this point should be part of dashed line
}

export interface iLineChartSeries {
    id: string;
    title: string;
    color: string;
    isActive?: boolean;
    data: iLineChartTimePoint[];
}

export interface iLineChartConfig {
    series: iLineChartSeries[];
    xAxisLabel?: string;
    yAxisLabel?: string;
    showTooltip?: boolean;
    dashedLineStartIndex?: number; // From which time point to start dashed line (>= 0 to enable)
}

export function getViewportGrid(
    { chartValues, height }: {
        chartValues: number[];
        height: number
    }
): {
    getY: (y: number) => number;
    yAxisValues: number[];
} {
    if (chartValues.length === 0) {
        return {
            getY: () => 0,
            yAxisValues: [0, 1],
        };
    }

    let maxValue = Math.max(...chartValues);
    let minValue = Math.min(...chartValues);

    // Add padding to both min and max (10%)
    const range = maxValue - minValue;
    const padding = range * 0.1;

    let min = minValue - padding;
    let max = maxValue + padding;

    // Determine step size based on the range
    const roughRange = max - min;
    let step: number;

    if (roughRange <= 5) {
        step = 1;
    } else if (roughRange <= 10) {
        step = 2;
    } else if (roughRange <= 25) {
        step = 5;
    } else if (roughRange <= 50) {
        step = 10;
    } else {
        step = Math.ceil(roughRange / 5 / 10) * 10; // Aim for ~5 steps
    }

    // Round min and max to nice numbers based on step
    min = Math.floor(min / step) * step;
    max = Math.ceil(max / step) * step;

    // Ensure min is not negative if all values are positive and close to zero
    if (minValue >= 0 && min < 0 && minValue < step) {
        min = 0;
    }

    const proportion = height / (max - min);

    function getY(value: number) {
        return (max - value) * proportion;
    }

    // Generate Y-axis labels
    const yAxisValues: number[] = [];
    for (let value = min; value <= max; value += step) {
        yAxisValues.push(value);
    }

    return {
        getY,
        yAxisValues,
    };
}

export interface iPoint {
    x: number;
    y: number;
}

// Helper function to create boundary points (start and end)
function createBoundaryPoints(
    series: iLineChartSeries,
    viewport: iViewBoxProps,
    getY: (value: number) => number
): Array<iPoint & { isActual?: boolean }> {
    const boundaryPoints: Array<iPoint & { isActual?: boolean }> = [];
    
    if (series.data.length > 0) {
        // Starting point at x=0
        const firstPoint = series.data[0].points[0];
        boundaryPoints.push({
            x: 0,
            y: getY(firstPoint.value),
            isActual: firstPoint.isActual ?? !series.data[0].isDashed
        });

        // Ending point at x=width
        const lastTimePoint = series.data.at(-1) as iLineChartTimePoint;
        const lastPoint = lastTimePoint.points.at(-1) as iLineChartDataPoint;
        boundaryPoints.push({
            x: viewport.width,
            y: getY(lastPoint.value),
            isActual: lastPoint.isActual ?? !lastTimePoint.isDashed
        });
    }
    
    return boundaryPoints;
}

// Helper function to collect all data points
function collectDataPoints(
    series: iLineChartSeries,
    viewport: iViewBoxProps,
    getY: (value: number) => number
): Array<iPoint & { isActual?: boolean }> {
    const timePointWidth = viewport.width / series.data.length;
    const dataPoints: Array<iPoint & { isActual?: boolean }> = [];

    for (const timePoint of series.data) {
        const timeIndex = series.data.indexOf(timePoint);
        const pointCount = timePoint.points.length;
        const pointWidth = timePointWidth / pointCount;

        for (const point of timePoint.points) {
            const pointIndex = timePoint.points.indexOf(point);
            const x = timePointWidth * timeIndex + pointWidth * (pointIndex + 0.5);
            dataPoints.push({
                x,
                y: getY(point.value),
                isActual: point.isActual ?? !timePoint.isDashed
            });
        }
    }

    return dataPoints;
}

// Helper function to process curve extensions
function processCurveExtensions(
    actualPoints: iPoint[],
    predictedPoints: iPoint[],
    allPoints: Array<iPoint & { isActual?: boolean }>,
    lastActualIndex: number,
    firstPredictedIndex: number,
    viewport: iViewBoxProps,
    series: iLineChartSeries
): void {
    const hasValidIndices = lastActualIndex >= 0 && firstPredictedIndex >= 0 && firstPredictedIndex > lastActualIndex;
    if (!hasValidIndices) return;

    const lastActualPoint = allPoints[lastActualIndex];
    const firstPredictedPoint = allPoints[firstPredictedIndex];
    const timePointWidth = viewport.width / series.data.length;
    const lastActualTimeIndex = Math.floor(lastActualPoint.x / timePointWidth);
    const firstPredictedTimeIndex = Math.floor(firstPredictedPoint.x / timePointWidth);
    
    if (lastActualTimeIndex === firstPredictedTimeIndex) {
        // Same time period - extend to midpoint
        const midX = (lastActualPoint.x + firstPredictedPoint.x) / 2;
        const midY = (lastActualPoint.y + firstPredictedPoint.y) / 2;
        actualPoints.push({ x: midX, y: midY });
        predictedPoints.unshift({ x: midX, y: midY });
    } else {
        // Different time periods - extend to period end
        const timePeriodEndX = (lastActualTimeIndex + 1) * timePointWidth;
        actualPoints.push({ x: timePeriodEndX, y: lastActualPoint.y });
        predictedPoints.unshift({ x: timePeriodEndX, y: lastActualPoint.y });
    }
}

// Get continuous line segments for actual and predicted data
export function getActualPredictedPoints(
    {
        series,
        viewport,
        getY,
    }: {
        series: iLineChartSeries;
        viewport: iViewBoxProps;
        getY: (value: number) => number;
    }
): { actualPoints: iPoint[]; predictedPoints: iPoint[] } {
    // Collect all points
    const boundaryPoints = createBoundaryPoints(series, viewport, getY);
    const dataPoints = collectDataPoints(series, viewport, getY);
    const allPoints = [...boundaryPoints.slice(0, 1), ...dataPoints, ...boundaryPoints.slice(1)];

    // Separate actual and predicted points
    const actualPoints: iPoint[] = [];
    const predictedPoints: iPoint[] = [];

    for (const point of allPoints) {
        (point.isActual ? actualPoints : predictedPoints).push({ x: point.x, y: point.y });
    }

    // Find transition indices and process extensions
    const { lastActualIndex, firstPredictedIndex } = findTransitionIndices(allPoints);
    processCurveExtensions(actualPoints, predictedPoints, allPoints, lastActualIndex, firstPredictedIndex, viewport, series);

    return { actualPoints, predictedPoints };
}

// Helper function to find transition indices
function findTransitionIndices(allPoints: Array<iPoint & { isActual?: boolean }>): {
    lastActualIndex: number;
    firstPredictedIndex: number;
} {
    let lastActualIndex = -1;
    let firstPredictedIndex = -1;
    
    for (let i = 0; i < allPoints.length; i++) {
        if (allPoints[i].isActual) {
            lastActualIndex = i;
        } else if (firstPredictedIndex === -1) {
            firstPredictedIndex = i;
        }
    }
    
    return { lastActualIndex, firstPredictedIndex };
}

export function getPath(points: iPoint[]): string {
    let result = '';

    for (const point of points) {
        const index = points.indexOf(point);
        result += ` ${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
    }

    return result;
}

// Helper function to get point type based on configuration
export function getPointType(
    timePoint: iLineChartTimePoint,
    pointIndex: number,
    isDashedSection: boolean
): PointType {
    const hasMultiplePoints = timePoint.points.length > 1;
    const point = timePoint.points[pointIndex];

    if (hasMultiplePoints) {
        // For sub-period data, check if individual point is actual or predicted
        if (point?.isActual !== undefined) {
            return point.isActual ? PointType.Solid : PointType.Empty;
        }
        // Fall back to section-based logic
        return isDashedSection ? PointType.Empty : PointType.Solid;
    } else {
        // Single point - use section-based logic
        return isDashedSection ? PointType.Empty : PointType.Small;
    }
}

// Helper function to determine if a time point should be in dashed section
export function isDashedSection(
    timeIndex: number,
    config: iLineChartConfig
): boolean {
    if (config.dashedLineStartIndex === undefined || config.dashedLineStartIndex < 0) {
        return false;
    }
    return timeIndex >= config.dashedLineStartIndex;
}

// Helper function to get all values from the chart data for y-axis calculation
export function getAllChartValues(config: iLineChartConfig): number[] {
    const allValues: number[] = [];

    for (const series of config.series) {
        for (const timePoint of series.data) {
            for (const point of timePoint.points) {
                allValues.push(point.value);
            }
        }
    }

    return allValues;
}